import { Component, Input, OnChanges, SimpleChanges, ViewEncapsulation, Output, EventEmitter, ViewChildren, QueryList, ElementRef } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";

import { BenchmarkDiagramPoint, BenchmarkDiagramSeries, BenchmarksModel, FcstAdjStrategy } from "../../shared/benchmarks/benchmarks-model";
import { DataService } from "../../shared/network/data.service";
import { NotificationService } from "../../shared/notificaton.service";
import { RoundingHelper } from "../../shared/rounding/rounding-helper";
import { FancyNumberPipe } from "../../shared/directives/fancy-number.pipe";
import { ForecastAdjustmentViewItem } from "../../shared/benchmarks/forecast-adjustment-view-item";
import { DriverType } from "../../shared/project/driver-types/driver-type";

// A node of the diagram, with what its markup needs from its series: nodes are drawn from diagramNodes, in an order
// of their own, and not along with the lines of their series
interface DiagramNode {
    series: BenchmarkDiagramSeries;
    seriesIx: number;
    point: BenchmarkDiagramPoint;
    pointIx: number;
}

@Component({
    selector: 'app-trend',
    templateUrl: './trend.component.html',
    styleUrls: ['./trend.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class TrendComponent implements OnChanges {
    FcstAdjStrategy: typeof FcstAdjStrategy = FcstAdjStrategy;
    DriverType: typeof DriverType = DriverType;
    @Input() model: BenchmarksModel;
    @Input() projectId: string;
    @Input() viewId: string;
    @Input() tabId: string | null;
    @Input() cardIndex: number | null;
    @Input() isMainfcstwalk: boolean = false;
    @Input() tabIx: number | null;
    // The page's SEQ/TOT switch: /cards has a single one, which its Walk, Summary, Trend and Related all show and set
    @Input() seqMode = true;
    @Output() seqModeChange = new EventEmitter<boolean>();
    @Output() formCompChanged = new EventEmitter<number>();
    @Output() saved = new EventEmitter<{ formCompIx: number }>();
    public selectedFormCompIx;
    public loading = true;
    public xlsxDownloadLink: string = '';
    public haveAdjustments = false;
    public isForecastAdjustmentDirty = false;
    public editingFcst = false;
    @ViewChildren("pointValInput") pointValInputs: QueryList<ElementRef>;

    public pointWidth = 70;
    public viewBoxWidth = 1000;
    public viewBoxHeight = 400;
    public leftMargin = 60; // Space for Y-axis labels
    public showLegendForArrows = false;
    public adjustedSeries: BenchmarkDiagramSeries = undefined;
    public showAdjustedForecast = false;

    public diagramMaxVal = 100;
    public diagramMinVal = 0;
    public xStep: number = 100;
    public yStep: number = 20;
    public verticalGridLines = [];  //fake array to loop through in the template
    public horizontalGridLines = [];
    public linearLeftX: number = undefined;
    public linearLeftY: number = undefined;
    public linearRightX: number = undefined;
    public linearRightY: number = undefined;
    public bandTetragon: string = undefined;
    public diagramNodes: DiagramNode[] = [];

    public formCompAdjCtx = {
        roundTo: 'auto',
        scale: 'auto',
        showPlusForPositive: true,
        use2stepRounding: true,
    };
    public curDriverformCompAdjCtx = FancyNumberPipe.createDefaultContext();
    public outcomeImpactCtx = FancyNumberPipe.createDefaultContext();
    public diagramPointCtx = FancyNumberPipe.createDefaultContext();
    public diagramMinMaxCtx = FancyNumberPipe.createDefaultContext();
    public percCtx = {
        showPlusForPositive: true,
        roundTo: 1,
        use2stepRounding: false,
    };
    public outcomeCtx = FancyNumberPipe.createDefaultContext();
    public formCompCtxs: any[] = [];

    public currentForecastAdjustment: ForecastAdjustmentViewItem;
    public columnNames: string[] = [];
    private modelSnapshotBeforeEditing: BenchmarksModel | null = null;

    public constructor(private dataService: DataService, private notifier: NotificationService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        //console.log('ngOnChanges', changes);
        if (this.isMainfcstwalk) {
            this.seqMode = false;
        }
        if (changes['model']) {
            if (this.model) {
                //console.log('TrendComponent: Model received', this.model);
                this.model = JSON.parse(JSON.stringify(this.model));
                this.initModel(this.model);
                // Every model selects its own variable: in the Trend gui-tab of /cards this component outlives its model
                // (other tabs, story trend links), and save() sends selectedFormCompIx along with the model's driver
                this.preselectFormComp(this.model);
                this.loadModel(this.model);
            } else {
                this.loading = true;
            }
        } else if (changes['seqMode'] && this.model) {
            // switched elsewhere on the page (Walk, Summary, Related); when it was this Trend's switch, setSeqMode has laid the
            // diagram out already and this does it once more
            this.loadModel(this.model);
        }
    }

    private preselectFormComp(model: BenchmarksModel): void {
        if (model.driverTypeName === model.outcomeName) {
            this.selectedFormCompIx = -1;
        } else {
            const matchingFormComp = model.sideView?.formulaComponents?.find(
                fc => fc.driverTypeName === model.driverTypeName
            );
            if (matchingFormComp) {
                this.selectedFormCompIx = matchingFormComp.formulaComponentIndex;
            } else {
                console.log('no mathching formComp found.');
                this.selectedFormCompIx = -1;
            }
        }
    }

    public getSelectedDriverTypeName(): string {
        if (!this.model) {
            return '';
        }
        if (this.selectedFormCompIx === -1) {
            return this.model.outcomeName;
        }
        const selectedFormComp = this.model.sideView?.formulaComponents?.find(
            fc => fc.formulaComponentIndex === this.selectedFormCompIx
        );
        return selectedFormComp ? selectedFormComp.driverTypeName : this.model.outcomeName;
    }

    public get isForecastEditingAllowed(): boolean {
        if (!this.model?.sideView?.formulaComponents || this.model.sideView.formulaComponents.length === 0) {
            return true;
        }
        return this.selectedFormCompIx !== -1;
    }

    private get activeSeries(): BenchmarkDiagramSeries[] {
        return this.seqMode ? this.model.diagramSeriesSeq : this.model.diagramSeriesTot;
    }

    public get showStrategyChoice(): boolean {
        return !!this.model && (this.seqMode ? this.model.haveDifferentMults : this.model.haveDifferentMultsTot);
    }

    public hasAdjustablePoints(): boolean {
        if (!this.model?.diagramSeriesSeq) {
            return false;
        }
        const series = this.activeSeries;
        for (let i = 0; i < series.length; i++) {
            if (series[i].points && series[i].points.some(point => point.isAdjustable)) {
                return true;
            }
        }
        return false;
    }

    public selectFormComp(formCompIx: number): void {
        if (this.selectedFormCompIx !== formCompIx) {
            this.selectedFormCompIx = formCompIx;
            this.editingFcst = false;
            this.formCompChanged.emit(formCompIx);
            if (!this.isForecastEditingAllowed) {
                this.editingFcst = false;
            }
        }
    }

    private initModel(model: BenchmarksModel): void {
        //console.log('initModel', model);
        let self = this;
        function initSeries(series: BenchmarkDiagramSeries[]) {
            for (let i = 0; i < series.length; ++i) {
                let originalSeries = self.findOriginalSeries(series, series[i]);
                for (let j = 0; j < series[i].points.length; ++j) {
                    series[i].points[j].originalValue = series[i].points[j].value;
                    if (series[i].isAdjustedForecast) {
                        self.haveAdjustments = true;
                        // the adjusted series has the forecast points only, its indices aren't those of the original series
                        let originalPoint = originalSeries?.points.find(p => p.controlValue == series[i].points[j].controlValue);
                        if (originalPoint && originalPoint.value != series[i].points[j].value) {
                            series[i].points[j].canBeDeleted = true;
                        }
                    }
                }
            }
        }
        initSeries(model.diagramSeriesTot);
        initSeries(model.diagramSeriesSeq);

        model.numericalColumns.splice(0, 0, { index: null, name: 'None' });
        model.diagramSeriesWithoutAdjusted = model.diagramSeriesSeq.filter(s => !s.isAdjustedForecast);
    }

    private loadModel(model: BenchmarksModel): void {
        // Sort series to render adjusted forecast series last (on top)
        model.diagramSeriesSeq.sort((a, b) => {
            if (a.isAdjustedForecast === b.isAdjustedForecast) return 0;
            return a.isAdjustedForecast ? 1 : -1;
        });
        model.diagramSeriesTot.sort((a, b) => {
            if (a.isAdjustedForecast === b.isAdjustedForecast) return 0;
            return a.isAdjustedForecast ? 1 : -1;
        });
        
        let series = this.seqMode ? model.diagramSeriesSeq : model.diagramSeriesTot;

        let allNums: number[] = [];
        for (let i = 0; i < series.length; ++i) {
            for (let j = 0; j < series[i].points.length; ++j) {
                if (series[i].points[j]) {
                    allNums.push(series[i].points[j].value);
                }
            }
        }
        let diagramRoundingSettings = RoundingHelper.CalcRoundingSettingsForDiagram(allNums);

        let minVal = diagramRoundingSettings.minAxis;
        let maxVal = diagramRoundingSettings.maxAxis;
        this.diagramMinMaxCtx = {
            roundTo: diagramRoundingSettings.roundTo,
            showPlusForPositive: false,
            cap: undefined,
            scale: diagramRoundingSettings.scale,
            use2stepRounding: true,
        };

        const minControlValsCountForWidth = 1;
        let width = 1;
        for (let i = 0; i < series.length; ++i) {
            for (let j = 0; j < series[i].points.length; ++j) {
                if (!series[i].points[j]) {
                    continue;
                }
                if (width < series[i].points[j].xPos + 1) {
                    width = series[i].points[j].xPos + 1;
                }
            }
        }
        let xStep = (this.viewBoxWidth - this.leftMargin) / (Math.max(width, minControlValsCountForWidth) + 0);
        let verticalGap = 25; // Add padding to ensure points at min/max values are fully visible
        let yStep = (this.viewBoxHeight - 2 * verticalGap) / (maxVal - minVal);

        let self = this;
        function convertY(y : number) {
            return self.viewBoxHeight - verticalGap - yStep * (y - minVal);
        }
        function convertX(x: number) {
            return self.leftMargin + xStep * (x + .5);
        }

        for (let i = 0; i < series.length; ++i) {
            for (let j = 0; j < series[i].points.length; ++j) {
                let curPoint = series[i].points[j];
                if (!curPoint) {
                    continue;
                }
                curPoint.x = convertX(curPoint.xPos);
                curPoint.y = convertY(curPoint.value);
            }
        }
        this.diagramNodes = this.orderDiagramNodes(series);
        for (let i = 0; i < model.forecastAdjustments.length; ++i) {
            if (model.forecastAdjustments[i].driverIdStr == model.driverId) {
                this.currentForecastAdjustment = model.forecastAdjustments[i];
            }
        }

        this.diagramMinVal = minVal;
        this.diagramMaxVal = maxVal;
        this.xStep = xStep;
        this.yStep = yStep;
        this.model = model;
        this.createGrid();
        if (series && series[0].bandTetragon) {
            let tetragon = series[0].bandTetragon;
            this.bandTetragon = `${convertX(tetragon.leftX)},${convertY(tetragon.topLeftY)} ${convertX(tetragon.rightX)},${convertY(tetragon.topRightY)} ${convertX(tetragon.rightX)},${convertY(tetragon.bottomRightY)} ${convertX(tetragon.leftX)},${convertY(tetragon.bottomLeftY)}`;
            let band = (tetragon.topLeftY - tetragon.bottomLeftY) / 2;
            this.linearLeftX = convertX(tetragon.leftX);
            //this.linearLeftY = convertY(tetragon.topLeftY - band);
            this.linearLeftY = convertY(tetragon.linearLeftY);
            this.linearRightX = convertX(tetragon.rightX);
            //this.linearRightY = convertY(tetragon.topRightY - band);
            this.linearRightY = convertY(tetragon.linearRightY);
        }
        //this.loadColumnNames(model.numericalColumns);
        this.createXlsxDownLoadLink();
        this.initFancyNumCtxs();
        this.loading = false;
    }

    private initFancyNumCtxs(): void {
        // Combine all outcome-related values for consistent rounding between outcomeCtx and outcomeImpactCtx
        let outcomeAndImpactNums: number[] = [
            this.model.sideView.totalOutcomeBeforeAdj,
            this.model.sideView.totalOutcomeAfterAdj
        ];
        if (this.model.sideView.formulaComponents) {
            outcomeAndImpactNums = outcomeAndImpactNums.concat(this.model.sideView.formulaComponents.filter(fc => fc.outcomeImpact).map(fc => fc.outcomeImpact));
            outcomeAndImpactNums = outcomeAndImpactNums.concat(this.model.sideView.formulaComponents.filter(fc => fc.outcomeImpactFromThisTab).map(fc => fc.outcomeImpactFromThisTab));
            outcomeAndImpactNums = outcomeAndImpactNums.concat(this.model.sideView.formulaComponents.filter(fc => fc.outcomeImpactFromPreviousTabs).map(fc => fc.outcomeImpactFromPreviousTabs));
        }
        outcomeAndImpactNums = outcomeAndImpactNums.filter(n => n !== undefined && n !== null);
        const outcomeRoundingSettings = RoundingHelper.CalcRoundingSettingsForNums(outcomeAndImpactNums);
        this.outcomeCtx = {
            roundTo: outcomeRoundingSettings.roundTo,
            scale: outcomeRoundingSettings.scale,
            showPlusForPositive: false,
            use2stepRounding: true,
        };
        this.outcomeImpactCtx = {
            roundTo: outcomeRoundingSettings.roundTo,
            scale: outcomeRoundingSettings.scale,
            showPlusForPositive: true,
            use2stepRounding: true,
        };

        this.formCompCtxs = [];
        if (this.model.sideView.formulaComponents) {
            for (let i = 0; i < this.model.sideView.formulaComponents.length; i++) {
                const formComp = this.model.sideView.formulaComponents[i];
                const numsForRounding = [
                    formComp.formCompBeforeAdj,
                    formComp.formCompAfterAdj,
                    formComp.prevTabDetail?.formCompBeforeAdj,
                    formComp.prevTabDetail?.formCompAfterAdj,
                    formComp.thisTabDetail?.formCompBeforeAdj,
                    formComp.thisTabDetail?.formCompAfterAdj
                ].filter(n => n !== undefined && n !== null);
                const roundingSettings = RoundingHelper.CalcRoundingSettingsForNums(numsForRounding);
                this.formCompCtxs[i] = {
                    roundTo: roundingSettings.roundTo,
                    scale: roundingSettings.scale,
                    showPlusForPositive: false,
                    use2stepRounding: true,
                };
            }
        }

        let curDriverformCompAdjNums = [];
        let curDriverFormCompAdj = this.model.sideView.formulaComponents.find(fc => fc.formCompAdjFromPreviousTabs || fc.formCompAdjFromThisTab);
        if (curDriverFormCompAdj) {
            if (curDriverFormCompAdj.formCompAdjFromPreviousTabs) {
                curDriverformCompAdjNums.push(curDriverFormCompAdj.formCompAdjFromPreviousTabs);
            }
            if (curDriverFormCompAdj.formCompAdjFromThisTab) {
                curDriverformCompAdjNums.push(curDriverFormCompAdj.formCompAdjFromThisTab);
            }
        }
        let curDriverformCompAdjRoundingSettings = RoundingHelper.CalcRoundingSettingsForNums(curDriverformCompAdjNums);
        this.curDriverformCompAdjCtx = {
            roundTo: curDriverformCompAdjRoundingSettings.roundTo,
            scale: curDriverformCompAdjRoundingSettings.scale,
            showPlusForPositive: true,
            use2stepRounding: true,
        };

        let diagramPointsNums = [ this.diagramMaxVal ];
        for (let i = 0; i < this.model.diagramSeriesSeq.length; ++i) {
            for (let j = 0; j < this.model.diagramSeriesSeq[i].points.length; ++j) {
                diagramPointsNums.push(this.model.diagramSeriesSeq[i].points[j].value);
            }
        }
        let diagramPointsRoundingSettings = RoundingHelper.CalcRoundingSettingsForNums(diagramPointsNums);
        this.diagramPointCtx = {
            roundTo: diagramPointsRoundingSettings.roundTo,
            scale: diagramPointsRoundingSettings.scale,
            showPlusForPositive: false,
            use2stepRounding: true,};
    }

    private createGrid(): void {
        this.verticalGridLines = new Array(Math.floor(this.viewBoxWidth / this.xStep) + 1);
        this.calculateHorizontalGridLines();
    }

    private calculateHorizontalGridLines(): void {
        const minVal = this.diagramMinVal;
        const maxVal = this.diagramMaxVal;
        const range = maxVal - minVal;

        if (range <= 0) {
            this.horizontalGridLines = [];
            return;
        }

        // Calculate a nice step size that gives us 3-8 lines
        const rawStep = range / 5; // Target ~5 lines initially
        const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const normalizedStep = rawStep / magnitude;

        // Round to nice numbers: 1, 2, 2.5, 5, 10
        let niceStep: number;
        if (normalizedStep <= 1) {
            niceStep = 1;
        } else if (normalizedStep <= 2) {
            niceStep = 2;
        } else if (normalizedStep <= 2.5) {
            niceStep = 2.5;
        } else if (normalizedStep <= 5) {
            niceStep = 5;
        } else {
            niceStep = 10;
        }

        let step = niceStep * magnitude;

        // Adjust step to ensure we have between 3 and 8 lines
        let lineCount = Math.floor(range / step) + 1;
        while (lineCount < 3 && step > magnitude / 10) {
            step = step / 2;
            lineCount = Math.floor(range / step) + 1;
        }
        while (lineCount > 8) {
            step = step * 2;
            lineCount = Math.floor(range / step) + 1;
        }

        // Calculate the starting value (first nice number >= minVal)
        const startValue = Math.ceil(minVal / step) * step;

        // Generate grid lines
        this.horizontalGridLines = [];
        const verticalGap = 25; // Match the verticalGap used in loadModel
        const yStep = (this.viewBoxHeight - 2 * verticalGap) / (maxVal - minVal);

        for (let value = startValue; value <= maxVal; value += step) {
            const y = this.viewBoxHeight - verticalGap - yStep * (value - minVal);
            this.horizontalGridLines.push({ value, y });
        }

        // Ensure we have at least 3 lines
        if (this.horizontalGridLines.length < 3) {
            // Recalculate with smaller step
            step = step / 2;
            const newStartValue = Math.ceil(minVal / step) * step;
            this.horizontalGridLines = [];
            for (let value = newStartValue; value <= maxVal; value += step) {
                const y = this.viewBoxHeight - verticalGap - yStep * (value - minVal);
                this.horizontalGridLines.push({ value, y });
            }
        }

        // Limit to 8 lines maximum
        if (this.horizontalGridLines.length > 8) {
            this.horizontalGridLines = this.horizontalGridLines.slice(0, 8);
        }
    }

    private findOriginalSeries(seriesList: BenchmarkDiagramSeries[], series: BenchmarkDiagramSeries): BenchmarkDiagramSeries {
        return this.findRelatedSeries(seriesList, series, false);
    }

    private findAdjustedSeries(seriesList: BenchmarkDiagramSeries[], series: BenchmarkDiagramSeries): BenchmarkDiagramSeries {
        return this.findRelatedSeries(seriesList, series, true);
    }

    private findRelatedSeries(seriesList: BenchmarkDiagramSeries[], series: BenchmarkDiagramSeries, adjusted: boolean): BenchmarkDiagramSeries {
        if (!seriesList) {
            return undefined;
        }
        return seriesList.find(s => s.name == series.name && s.isAdjustedForecast === adjusted);
    }

    // SVG has no z-index: a node covers the nodes drawn before it. An adjusted forecast point shares its x with its original,
    // so their nodes overlap when the values are close, and which one has to be on top depends on the point:
    //  - it is adjusted: the adjusted node goes above, to be seen and clicked however small the adjustment is;
    //  - it isn't: the original stays above, or every forecast point would look adjusted. Unless the original can't be edited
    //    while the adjusted point can (the values differ past what a JS number holds): the editable one stays above then.
    private orderDiagramNodes(series: BenchmarkDiagramSeries[]): DiagramNode[] {
        const belowOriginals: DiagramNode[] = [];
        const originals: DiagramNode[] = [];
        const adjusted: DiagramNode[] = [];
        for (let i = 0; i < series.length; ++i) {
            const originalSeries = series[i].isAdjustedForecast ? this.findOriginalSeries(series, series[i]) : undefined;
            for (let j = 0; j < series[i].points.length; ++j) {
                const point = series[i].points[j];
                const node: DiagramNode = { series: series[i], seriesIx: i, point: point, pointIx: j };
                if (!series[i].isAdjustedForecast) {
                    originals.push(node);
                    continue;
                }
                const originalPoint = originalSeries?.points.find(p => p.controlValue == point.controlValue);
                if (originalPoint && originalPoint.value == point.value && (originalPoint.isAdjustable || !point.isAdjustable)) {
                    belowOriginals.push(node);
                } else {
                    adjusted.push(node);
                }
            }
        }
        return belowOriginals.concat(originals, adjusted);
    }

    // loadModel rebuilds diagramNodes: keep the DOM of each node, a click must not lose its target to a re-render
    public trackDiagramNode(index: number, node: DiagramNode): BenchmarkDiagramPoint {
        return node.point;
    }

    public startEditingFcst() {
        if (!this.isForecastEditingAllowed) {
            this.notifier.showWarning('Forecast editing is only available for formula components');
            return;
        }
        this.modelSnapshotBeforeEditing = JSON.parse(JSON.stringify(this.model));
        this.editingFcst = true;
    }

    public cancelEditingFcst() {
        if (this.modelSnapshotBeforeEditing && this.isForecastAdjustmentDirty) {
            // Restore the model to its state before editing started
            this.model = this.modelSnapshotBeforeEditing;
            this.initModel(this.model);
            this.loadModel(this.model);
            this.isForecastAdjustmentDirty = false;
        }
        this.modelSnapshotBeforeEditing = null;
        this.editingFcst = false;
    }

    private createXlsxDownLoadLink() {
    }

    public setSeqMode(val: boolean): void {
        if (this.isMainfcstwalk) {
            return;
        }
        if (this.seqMode !== val) {
            this.seqMode = val;
            if (this.model) {
                this.loadModel(this.model);
            }
            this.seqModeChange.emit(val);
        }
    }

    pointClick(point: BenchmarkDiagramPoint) {
        if (!this.editingFcst || !point.isAdjustable) {
            return;
        }
        point.editing = true;
        setTimeout(() => {
            let firstInput = this.pointValInputs.toArray()[0];
            firstInput.nativeElement.focus();
            firstInput.nativeElement.select();
            firstInput.nativeElement.scrollLeft = 0;
        }, 1);
    }

    applyAdjustment(seriesIx: number, point: BenchmarkDiagramPoint) {
        this.isForecastAdjustmentDirty = true;
        point.value = +point.value;
        let activeSeries = this.activeSeries;
        let series = activeSeries[seriesIx];
        let adjustedSeries = this.findAdjustedSeries(activeSeries, series);
        if (!series.isAdjustedForecast && !adjustedSeries) {
            if (point.value != point.originalValue) {
                let adjSeries: BenchmarkDiagramSeries = {
                    name: series.name,
                    forecastNameSuffix: ' (adj. forecast)',
                    color: series.adjColor,
                    adjColor: series.adjColor,
                    isAdjustedForecast: true,
                    points: [],
                    minVal: -1,
                    maxVal: -1,
                    oldControlValX: -1,
                    oldControlValY: -1,
                    newControlValX: -1,
                    newControlValY: -1,
                    bandTetragon: undefined,
                    bandTetragonStr: undefined,
                };
                for (let i = 0; i < series.points.length; ++i) {
                    let curPoint = series.points[i];
                    if (!curPoint.isSelectedAsForecast) {
                        continue;
                    }
                    let newVal = curPoint.controlValue === point.controlValue ? point.value : curPoint.value;
                    let newPoint: BenchmarkDiagramPoint = {
                        canBeDeleted: newVal != curPoint.originalValue,
                        controlValue: curPoint.controlValue,
                        value: newVal,
                        originalValue: curPoint.originalValue,
                        editing: false,
                        isAdjustable: curPoint.isAdjustable,
                        xPos: curPoint.xPos,
                        x: -1,
                        y: -1,
                        isSelectedAsForecast: true,
                        showArrows: false,
                    };
                    curPoint.isAdjustable = curPoint.isSelectedAsForecast;
                    adjSeries.points.push(newPoint);
                }
                point.isAdjustable = point.isSelectedAsForecast && this.pointsAreTooClose(point.value, point.originalValue);
                point.value = point.originalValue;
                activeSeries.splice(seriesIx, 0, adjSeries);
            }
        } else if (!series.isAdjustedForecast && adjustedSeries) {
            var pt = adjustedSeries.points.find(p => p.controlValue == point.controlValue);
            pt.value = point.value;
            pt.canBeDeleted = true;
            point.isAdjustable = point.isSelectedAsForecast && this.pointsAreTooClose(point.value, point.originalValue);
            point.value = point.originalValue;
        }
        else {
            let originalSeries = this.findOriginalSeries(activeSeries, series);
            let originalPoint = originalSeries.points.find(p => p.controlValue == point.controlValue);
            point.canBeDeleted = point.value != originalPoint.value;
            originalPoint.isAdjustable = originalPoint.isSelectedAsForecast && this.pointsAreTooClose(point.value, originalPoint.value);
        }
        point.editing = false;
        this.loadModel(this.model);
    }

    private pointsAreTooClose(val1: number, val2: number): boolean {
        return Math.abs(val1 - val2) < (this.diagramMaxVal - this.diagramMinVal) * 0.04;
    }

    removeForecastAdjust(seriesIx: number, pointIx: number) {
        let activeSeries = this.activeSeries;
        let point = activeSeries[seriesIx].points[pointIx];
        if (point.canBeDeleted) {
            let originalSeries = this.findOriginalSeries(activeSeries, activeSeries[seriesIx]);
            let originalPoint = originalSeries.points.find(p => p.controlValue == point.controlValue);
            point.value = originalPoint.value;
            point.canBeDeleted = false;
            originalPoint.isAdjustable = true;
            this.applyAdjustment(seriesIx, point);
        }
    }

    async save(strategy: FcstAdjStrategy) {
        if (this.loading || !this.isForecastEditingAllowed) {
            return;
        }
        try {
            if (!this.isForecastAdjustmentDirty) {
                this.editingFcst = false;
                return;
            }
            this.loading = true;
            let adjustments = [];
            let activeSeries = this.activeSeries;
            for (let i = 0; i < activeSeries.length; ++i) {
                let series = activeSeries[i];
                if (!series.isAdjustedForecast) {
                    continue;
                }
                for (let j = 0; j < series.points.length; ++j) {
                    let point = series.points[j];
                    adjustments.push({
                        controlValue: point.controlValue,
                        adjustedValue: point.value,
                    });
                }
            }
            if (this.isMainfcstwalk) {
                await this.dataService.post<any>('benchmarks/saveForecastAdjustmentsForMainFcstWalk', {
                    projectId: this.projectId,
                    formCompIx: this.selectedFormCompIx,
                    tabIx: this.tabIx,
                    cardIndex: this.cardIndex,
                    adjustments: adjustments,
                    strategy: strategy,
                    isSeq: this.seqMode,
                });
                this.isForecastAdjustmentDirty = false;
                this.modelSnapshotBeforeEditing = null;
                this.saved.emit({ formCompIx: this.selectedFormCompIx });
            } else {
                await this.dataService.post<any>('benchmarks/saveForecastAdjustments', {
                    viewId: this.viewId,
                    driverId: this.model.driverId,
                    adjustments: adjustments,
                    formCompIx: this.selectedFormCompIx,
                    tabId: this.tabId,
                    cardIndex: this.cardIndex,
                    strategy: strategy,
                    isSeq: this.seqMode,
                });
                this.isForecastAdjustmentDirty = false;
                this.modelSnapshotBeforeEditing = null;
                this.editingFcst = false;
                this.saved.emit({ formCompIx: this.selectedFormCompIx });
            }
        } finally {
            this.loading = false;
        }
    }

    async undo() {
        if (this.loading || !this.model.undoAvailable) {
            return;
        }
        try {
            this.loading = true;
            await this.dataService.post('processing/UndoMainFcstWalk', {
                projectId: this.projectId,
                loadMainFcstWalkModel: false,
            });
            this.saved.emit({ formCompIx: this.selectedFormCompIx });
        } finally {
            this.loading = false;
        }
    }

    async redo() {
        if (this.loading || !this.model.redoAvailable) {
            return;
        }
        try {
            this.loading = true;
            await this.dataService.post('processing/RedoMainFcstWalk', {
                projectId: this.projectId,
                loadMainFcstWalkModel: false,
            });
            this.saved.emit({ formCompIx: this.selectedFormCompIx });
        } finally {
            this.loading = false;
        }
    }

}
